import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Activity, AlertTriangle, CheckCircle2, Heart, TrendingDown, TrendingUp } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const Assessment = () => {
  const location = useLocation();
  const assessmentResult = location.state?.assessmentResult;

  if (!assessmentResult) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold">No Assessment Found</h1>
          <p className="text-muted-foreground">Please take a new assessment to see your health report.</p>
          <Button asChild>
            <Link to="/new-assessment">Take Assessment</Link>
          </Button>
        </div>
      </div>
    );
  }

  const { riskScore, riskLevel, riskFactors, recommendations } = assessmentResult;
  const riskColor = riskScore >= 70 ? "text-accent" : riskScore >= 40 ? "text-yellow-500" : "text-primary";

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Activity className="h-6 w-6 text-primary" />
              <span className="text-xl font-bold">HealthAI</span>
            </Link>
            <Button variant="outline" size="sm" className="rounded-full" asChild>
              <Link to="/dashboard">Back to Dashboard</Link>
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold mb-2">Your AI Health Report</h1>
          <p className="text-muted-foreground text-lg">A comprehensive analysis of your health profile.</p>
        </div>

        {/* Overall Risk Score */}
        <div className="mb-8 animate-scale-in">
          <Card className="rounded-2xl border-0 shadow-xl overflow-hidden">
            <div className="gradient-calm h-2" />
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl">Overall Health Risk Score</CardTitle>
              <CardDescription>This score represents your estimated health risk based on the provided data.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-6xl font-bold ${riskColor}`}>{riskScore}%</div>
                  <p className="text-lg text-muted-foreground mt-2">
                    Risk Level: <span className={`font-semibold ${riskColor}`}>{riskLevel}</span>
                  </p>
                </div>
                <div className={`h-32 w-32 rounded-full ${riskColor.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                  {riskLevel === "High" ? (
                    <AlertTriangle className={`h-16 w-16 ${riskColor}`} />
                  ) : (
                    <Heart className={`h-16 w-16 ${riskColor}`} />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Low Risk</span>
                  <span>High Risk</span>
                </div>
                <Progress value={riskScore} className="h-3" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risk Factors */}
        <div className="mb-8 animate-slide-up">
          <h2 className="text-2xl font-semibold mb-4">Key Risk Factors</h2>
          <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-3">
            {riskFactors.map((item, index) => (
              <Card key={index} className="rounded-2xl border-0 shadow-sm hover:shadow-lg transition-all card-hover">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg">{item.factor}</CardTitle>
                    {item.trend === "up" ? (
                      <TrendingUp className="h-5 w-5 text-accent" />
                    ) : item.trend === "down" ? (
                      <TrendingDown className="h-5 w-5 text-primary" />
                    ) : (
                      <Activity className="h-5 w-5 text-secondary" />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-3xl font-bold">{item.score}%</div>
                  <Progress value={item.score} className="h-2" />
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="animate-slide-up" style={{ animationDelay: "0.1s" }}>
          <Card className="rounded-2xl border-0 shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Personalized Recommendations</CardTitle>
              <CardDescription>Actionable steps to improve your health score and reduce risks.</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-foreground">{rec}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex gap-3">
                <Button className="rounded-xl" asChild>
                  <Link to="/nutrition">View Nutrition Plan</Link>
                </Button>
                <Button variant="outline" className="rounded-xl" asChild>
                  <Link to="/fitness">See Fitness Plan</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center mt-8">
          <Button asChild>
            <Link to="/new-assessment">Take New Assessment</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Assessment;
